import React, { useEffect, useState } from "react";
import "./style.css";
import API from "../../../API";
import CouncilsAPI from "../../../API/councils";
import { Button, Form, Input, Select } from "antd";
import { useCallback } from "react";
import { useAuth } from "../../../auth/AuthContext";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const validateMessages = {
  required: "label is required!",
  types: {
    email: "label is not a valid email!",
  },
};

const UsersForm = ({ onFinish, onCancel, editableUser, form, isSubmitting }) => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showProcuringEntityDropdown, setShowProcuringEntityDropdown] =
    useState(false);
  const [projects, setProjects] = useState([]);
  const [procuringEntities, setProcuringEntities] = useState([]);
  const { permissions: userPermissions } = useAuth();

  const checkEditedUserRole = useCallback(() => {
    if (editableUser) {
      const permissions = editableUser?.roles?.map((role) => {
        return role.permissions;
      })[0];

      if (Array.isArray(permissions)) {
        const userPermissions = permissions.map((permission) => {
          return permission.name;
        });

        const hasPitRole = editableUser?.roles?.some(role => ['pit', 'pit-focal-person'].includes(role.name));
        
        const canReadProject = userPermissions.includes("can read project");
        setShowProjectDropdown(canReadProject || hasPitRole);

        setShowProcuringEntityDropdown(hasPitRole);
      }
    }
  }, [editableUser, showProcuringEntityDropdown]);
  
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await API.get("roles");
        let availableRoles = response.data;
        
        if (userPermissions.includes('can create user')) {
          availableRoles = response.data;
        } else if (userPermissions.includes('can create pct user')) {
          availableRoles = response.data.filter(role => role.name !== 'admin');
        } else if (userPermissions.includes('can create pit user')) {
          availableRoles = response.data.filter(role => 
            ['pit', 'pit-focal-person'].includes(role.name)
          );
        } else {
          availableRoles = [];
        }
        
        setRoles(availableRoles);
      } catch (error) {
        console.error("Failed to fetch roles", error);
      }
    };
    fetchRoles();
  }, [userPermissions]);

  useEffect(() => {
    if (editableUser) {
      form.setFieldsValue({
        ...editableUser,
        roles: editableUser ? editableUser.roles.map(({ name }) => name) : [],
        project: editableUser
          ? editableUser.projects.map((project) => project.id)
          : [],
        council_id: editableUser ? editableUser?.councils[0]?.id : "",
      });
      checkEditedUserRole();
    }
  }, [checkEditedUserRole, editableUser, form]);

  useEffect(() => {
    if (showProjectDropdown || showProcuringEntityDropdown) {
      fetchProjects();
    }
  }, [showProjectDropdown, showProcuringEntityDropdown]);

  useEffect(() => {
    if (roles) {
      checkEditedUserRole();
    }
  }, [checkEditedUserRole, roles]);

  const fetchProjects = async () => {
    try {
      const response = await API.getPackageProjects();
      const projectNames = response.data.data.map((project) => project);

      setProjects(projectNames);
    } catch (error) {
      console.error("Failed to fetch projects", error);
    }
  };
  
  const fetchCouncils = useCallback(async () => {
  try {
    if (editableUser) {
      if (editableUser.councils && editableUser.councils.length > 0) {
        setProcuringEntities(editableUser.councils);
        return;
      }
    }
    setProcuringEntities([]);
  } catch (error) {
    console.error("Failed to fetch councils", error);
  }
}, [editableUser]);

  useEffect(() => {
    if (showProcuringEntityDropdown) {
      fetchCouncils();
    }
  }, [fetchCouncils, showProcuringEntityDropdown]);


  const handleRoleChange = (selectedRoleNames) => {
  
    const selectedRoles = roles.filter((role) =>
      selectedRoleNames.includes(role.name)
    );

    const extractedPermissions = selectedRoles.flatMap(({ permissions }) =>
      permissions.map(({ name }) => name)
    );
    setPermissions(extractedPermissions);

    const hasPitRole = selectedRoleNames.some(role => ['pit', 'pit-focal-person'].includes(role));
    
    const canReadProject = extractedPermissions.includes("can read project");
    setShowProjectDropdown(
      extractedPermissions.includes("can create project") || canReadProject || hasPitRole
    );

    // Show council dropdown ONLY for pit and pit-focal-person roles
    setShowProcuringEntityDropdown(hasPitRole);
  };



  const handleOnCancel = () => {
    onCancel(form);
    form.resetFields();
  };

  const handleProjectChange = async (projectId, form) => {
    
    if (projectId && (Array.isArray(projectId) ? projectId.length > 0 : true)) {
      try {
        const projectIds = Array.isArray(projectId) ? projectId : [projectId];
        const allCouncils = [];
        
        for (const id of projectIds) {
          const response = await CouncilsAPI.getProjectCouncils(id);
          
          if (response.success && response.data && response.data.data) {
            allCouncils.push(...response.data.data);
          }
        }
        
        const uniqueCouncils = allCouncils.filter((council, index, self) => 
          index === self.findIndex(c => c.id === council.id)
        );
        
        form.setFieldsValue({ council_id: undefined });
        setProcuringEntities(uniqueCouncils);
      } catch (error) {
        setProcuringEntities([]);
        form.setFieldsValue({ council_id: undefined });
      }
    } else {
      setProcuringEntities([]);
      form.setFieldsValue({ council_id: undefined });
    }
  };

  return (
    <Form
      form={form}
      className="user-form"
      data-testid="user-form"
      {...layout}
      name="user-form"
      onFinish={onFinish}
      validateMessages={validateMessages}
      initialValues={{}}
    >
      <Form.Item
        name="first_name"
        label="First Name"
        rules={[{ required: true }]}
      >
        <Input placeholder="First Name" data-testid="user-first-name-input" />
      </Form.Item>

      <Form.Item
        name="last_name"
        label="Last Name"
        rules={[{ required: true }]}
      >
        <Input placeholder="Last Name" data-testid="user-last-name-input" />
      </Form.Item>

      <Form.Item
        name="email"
        label="Email"
        rules={[{ required: true, type: "email" }]}
      >
        <Input 
          placeholder="Email" 
          data-testid="user-email-input"
          disabled={!!editableUser}
          title={editableUser ? "Email cannot be changed" : ""}
        />
      </Form.Item>

      <Form.Item name="phone" label="Phone Number">
        <Input placeholder="Phone Number" data-testid="user-phone-input" />
      </Form.Item>

      <Form.Item name="title" label="Title" rules={[{ required: true }]}>
        <Input placeholder="Title" data-testid="user-title-input" />
      </Form.Item>

      <Form.Item name="roles" label="Role" rules={[{ required: true }]}>
        <Select
          mode="multiple"
          placeholder="Select Role(s)"
          onChange={handleRoleChange}
          data-testid="user-role-select"
        >
          {roles.map((role) => (
            <Select.Option
              key={role.id}
              value={role.name}
              data-testid={`user-role-option-${role.name}`}
            >
              {role.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {(showProjectDropdown || showProcuringEntityDropdown) && (
        <Form.Item
          name="project"
          label="Project"
          rules={[{ required: true, message: "Please select a project!" }]}
        >
          <Select
            placeholder="Select Project"
            mode={"multiple"}
            onChange={(value) => handleProjectChange(value, form)}
            data-testid="user-project-select"
          >
            {projects?.map((project, index) => (
              <Select.Option
                key={index}
                value={project.id}
                data-testid={`user-project-option-${project.id}`}
              >
                {project.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {showProcuringEntityDropdown && (
        <Form.Item
          name="council_id"
          label="Council"
          rules={[{ required: true, message: "Please select a Council!" }]}
        >
          <Select
            placeholder="Select Council"
            showSearch
            optionFilterProp="children"
            disabled={!projects || projects.length === 0}
            data-testid="user-council-select"
          >
            {procuringEntities?.map((entity, index) => (
              <Select.Option
                key={index}
                value={entity.id}
                data-testid={`user-council-option-${entity.id}`}
              >
                {entity.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {!editableUser && (
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#e6f7ff', 
          border: '1px solid #91d5ff', 
          borderRadius: '6px',
          marginBottom: '16px'
        }}>
          <p style={{ margin: 0, color: '#0050b3' }}>
            🎉 New users will receive an invitation email to set their password. No need to specify a password here!
          </p>
        </div>
      )}
      

      <div className="user-form-action-buttons">
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            data-testid="user-form-submit-button"
            loading={isSubmitting}
            disabled={isSubmitting}
            style={{ minWidth: '208px' }}
          >
            {editableUser ? 'Update User' : 'Create User & Send Invite'}
          </Button>
        </Form.Item>
        <Form.Item>
          <Button onClick={handleOnCancel} data-testid="cancel-user-form">
            Cancel
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default UsersForm;
